import { useGetIdentity, useGetList, useTranslate } from "ra-core";
import { useState } from "react";
import { cn } from "@/lib/utils";

import type { TaskCard } from "../types";
import { FunnelShell } from "./shared";
import { TaskRow } from "./TaskRow";
import { taskTiming, type TaskTiming } from "./todayLogic";

type Tab = "today" | "overdue" | "all";

const SECTIONS: { timing: TaskTiming; key: string }[] = [
  { timing: "overdue", key: "crm.tasks_page.overdue" },
  { timing: "today", key: "crm.tasks_page.today" },
  { timing: "later", key: "crm.tasks_page.later" },
  { timing: "none", key: "crm.tasks_page.no_date" },
];

/** My open tasks (docs/design/TasksM.dc.html) */
export const TasksPage = () => {
  const translate = useTranslate();
  const { identity } = useGetIdentity();
  const [tab, setTab] = useState<Tab>("today");
  const now = new Date();
  const { data: tasks = [] } = useGetList<TaskCard>(
    "task_cards",
    {
      filter: { "done_date@is": null, sales_id: identity?.id },
      pagination: { page: 1, perPage: 500 },
      sort: { field: "due_date", order: "ASC" },
    },
    { enabled: identity?.id != null },
  );
  const visible: Record<Tab, TaskTiming[]> = {
    today: ["overdue", "today"],
    overdue: ["overdue"],
    all: ["overdue", "today", "later", "none"],
  };
  const groups = SECTIONS.filter((s) => visible[tab].includes(s.timing))
    .map((s) => ({
      ...s,
      tasks: tasks.filter((t) => taskTiming(t.due_date, now) === s.timing),
    }))
    .filter((g) => g.tasks.length > 0);

  return (
    <FunnelShell title={translate("crm.tasks_page.title")}>
      <div
        role="tablist"
        className="mb-4 grid grid-cols-3 rounded-xl border bg-card p-1 text-sm md:max-w-xl"
      >
        {(["today", "overdue", "all"] as const).map((value) => (
          <button
            key={value}
            type="button"
            role="tab"
            aria-selected={tab === value}
            onClick={() => setTab(value)}
            className={cn(
              "rounded-lg py-2 font-semibold",
              tab === value
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground",
            )}
          >
            {translate(`crm.tasks_page.tab_${value}`)}
          </button>
        ))}
      </div>
      <div className="flex max-w-5xl flex-col gap-5">
        {groups.map((group) => (
          <section key={group.timing} className="flex flex-col gap-3">
            <h2 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
              {translate(group.key)}
            </h2>
            {group.tasks.map((task) => (
              <TaskRow key={task.id} task={task} />
            ))}
          </section>
        ))}
        {groups.length === 0 ? (
          <p className="text-muted-foreground">
            {translate("crm.tasks_page.empty")}
          </p>
        ) : null}
      </div>
    </FunnelShell>
  );
};
